import { CaptableSource, CaptableType, ICaptableDataModel } from '../../data-models/captable2.data-model';
import { MaggieHttp } from '../MaggieHttp';

/** Classified change type of a version vs. its predecessor (FD-4108). Mirrors the BE enum. */
export enum CaptableChangeType {
  Genesis = 'genesis',
  LiquidityExit = 'liquidity_exit',
  PricedRound = 'priced_round',
  ConvertibleFinancing = 'convertible_financing',
  Conversion = 'conversion',
  Recapitalization = 'recapitalization',
  SecondaryTransfer = 'secondary_transfer',
  OptionPoolAdjustment = 'option_pool_adjustment',
  ValuationUpdate = 'valuation_update',
  RestatementCorrection = 'restatement_correction',
  NoMaterialChange = 'no_material_change',
  NeedsReview = 'needs_review',
}

/** Whether a version's classification came from the classifier or a manual edit (FD-4108). */
export enum ClassificationProvenance {
  Auto = 'auto',
  Manual = 'manual',
}

/**
 * A single effective-dated cap table version (FD-3823). Each version is a selectable cap table,
 * identified by `id`; fetch its full data via {@link getCaptableById}.
 */
export interface ICaptableVersion {
  id: number;
  uuid: string;
  source: CaptableSource;
  type: CaptableType;
  /** The version's own effective date (YYYY-MM-DD). */
  effectiveFrom: string;
  /** Day before the next-later version's effective date; `null` for the latest version ("Present"). */
  effectiveTo: string | null;
  /** BE-formatted range label, e.g. "Effective from 2024-01-15 – Present". */
  label: string;
  /** Latest version in its (source, type) group. */
  isCurrent: boolean;
  /** Stored primary flag for the source. */
  isPrimary: boolean;
  /** Meaningful only for deal-docs / user rows; null or a constant string for other sources. */
  name: string | null;
  fullyDilutedShares: number | null;
  outstandingShares: number | null;
  cashRaised: number | null;
  reconcileStatus?: string | null;
  currencyId: number;
  updatedAt?: string;
  updatedBy?: string;
  /** Classified change type vs. the prior version; absent until classified (FD-4108). */
  changeType?: CaptableChangeType;
  /** Rendered change label, e.g. "Series B Close". */
  changeLabel?: string;
  /** Change subtype when applicable, e.g. "SAFE" or "Expansion". */
  changeSubtype?: string;
  /** Classifier tripped the confidence gate; needs a human. */
  needsReview?: boolean;
  /** Backdated version; prior-period figures changed after the fact. */
  restatedPeriod?: boolean;
  /** `auto` = classifier output; `manual` = user-edited (never auto-overwritten). */
  classificationProvenance?: ClassificationProvenance;
  /** Optional AI prose summary of the change. */
  summary?: string;
  /** Anchor snapshot carrying the classification — PATCH edits target this id, not `id` (FD-4176). */
  classificationCaptableId?: number;
}

/**
 * Edits accepted by the classification endpoint (FD-4108). The BE keys are `label`/`subtype`
 * (not `changeLabel`/`changeSubtype`); at least one field must be set (empty body -> 422).
 */
export interface IClassificationUpdate {
  changeType?: CaptableChangeType;
  label?: string;
  subtype?: string;
}

/** A source's ordered version timeline. Keyed by (source, type); versions are most-recent first. */
export interface ICaptableVersionGroup {
  source: CaptableSource;
  type: CaptableType;
  versions: ICaptableVersion[];
}

export async function getCaptableById(id: number): Promise<ICaptableDataModel> {
  const { data } = await MaggieHttp.maggie.get<ICaptableDataModel>(`/capTable/${id}`, {
    params: {
      version: 2,
    },
  });
  return data;
}

export async function getCapTablesForCompany(companyId: number): Promise<ICaptableDataModel[]> {
  const { data } = await MaggieHttp.maggie.get<ICaptableDataModel[]>(`/company/${companyId}/captables`);
  return data;
}

/**
 * Fetches a company's cap tables as per-source, effective-dated version timelines (FD-3823).
 * Company with no cap tables → `[]`.
 */
export async function getCaptableVersions(companyId: number): Promise<ICaptableVersionGroup[]> {
  const { data } = await MaggieHttp.maggie.get<ICaptableVersionGroup[] | ICaptableVersion[]>(
    `/capTable/company/${companyId}/versions`
  );
  return normalizeCaptableVersions(data);
}

/**
 * Normalizes the versions response into `(source, type)` groups. Passes a grouped response through
 * unchanged; if a flat version list arrives it groups client-side, preserving the most-recent-first
 * order the BE returns.
 */
export function normalizeCaptableVersions(
  raw: ICaptableVersionGroup[] | ICaptableVersion[] | null | undefined
): ICaptableVersionGroup[] {
  if (!raw?.length) {
    return [];
  }
  if ('versions' in raw[0]) {
    return raw as ICaptableVersionGroup[];
  }
  const groups = new Map<string, ICaptableVersionGroup>();
  for (const version of raw as ICaptableVersion[]) {
    const key = `${version.source}:${version.type}`;
    let group = groups.get(key);
    if (!group) {
      group = { source: version.source, type: version.type, versions: [] };
      groups.set(key, group);
    }
    group.versions.push(version);
  }
  return [...groups.values()];
}

export async function createCaptable(
  payload: Partial<ICaptableDataModel>,
  preview = false
): Promise<ICaptableDataModel> {
  const { data } = await MaggieHttp.maggie.post<Partial<ICaptableDataModel>, ICaptableDataModel>(
    `/capTable`,
    payload,
    {
      params: {
        preview,
        version: 2,
      },
    }
  );
  return data;
}

export async function updateCaptable(payload: Partial<ICaptableDataModel>): Promise<ICaptableDataModel> {
  const { data } = await MaggieHttp.maggie.put<Partial<ICaptableDataModel>, ICaptableDataModel>(
    `/capTable/${payload.id}`,
    payload,
    {
      params: {
        version: 2,
      },
    }
  );
  return data;
}

export async function makeCaptablePrimary(id: number): Promise<ICaptableDataModel> {
  const { data } = await MaggieHttp.maggie.put<undefined, ICaptableDataModel>(`/capTable/${id}/set-primary`);
  return data;
}

export async function updatePrimaryCaptableSource(companyId: number, source: CaptableSource): Promise<void> {
  await MaggieHttp.maggie.put(`/company/${companyId}/primaryCaptableSource`, { source });
}

export async function deleteCaptable(id: number): Promise<void> {
  await MaggieHttp.maggie.delete(`/capTable/${id}`);
}

export async function duplicateCaptable(
  id: number,
  params?: { version?: number }
): Promise<ICaptableDataModel> {
  const { data } = await MaggieHttp.maggie.post<undefined, ICaptableDataModel>(
    `/capTable/${id}/duplicate`,
    undefined,
    { params }
  );
  return data;
}

export async function checkCaptableExists(companyId: number): Promise<boolean> {
  const { data } = await MaggieHttp.maggie.get<boolean>(`/capTable/company/${companyId}/present`);
  return data;
}

/**
 * Overrides a version's auto-classified change type/label (FD-4108); the BE marks the classification
 * `manual` so the classifier never overwrites it. Requires the cap-table edit permission. Refetch the
 * timelines afterwards (e.g. via `useInvalidateCaptables`) to surface the update.
 */
export async function updateVersionClassification(
  captableId: number,
  payload: IClassificationUpdate
): Promise<void> {
  await MaggieHttp.maggie.patch<IClassificationUpdate, unknown>(
    `/capTable/versions/${captableId}/classification`,
    payload
  );
}
